import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  Target,
  BarChart3,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const ISO17020_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            ISO/IEC 17020 Certification
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Conformity assessment — Requirements for the operation of various
            types of bodies performing inspection.
          </p>
        </motion.div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              ISO/IEC 17020 Certification Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              ISO/IEC 17020:2012 is the international standard for Inspection
              Bodies, specifying the requirements for the competence of
              organizations performing inspections. It covers all types of
              inspections, whether conducted in-house, at suppliers’ premises,
              or at remote locations. The standard helps ensure that inspection
              bodies consistently deliver accurate, impartial, and reliable
              results, contributing to the trust and confidence of stakeholders,
              clients, and regulatory bodies.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              Achieving ISO/IEC 17020 certification demonstrates that an
              inspection body has met the rigorous quality management and
              technical competence requirements outlined by the standard. This
              certification is essential for organizations that provide
              inspection services in fields such as engineering, manufacturing,
              construction, environmental monitoring, and food safety, among
              others.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is ISO/IEC 17020 Certification?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              ISO/IEC 17020 certification sets the standard for inspection
              bodies, ensuring that they have the necessary competence to carry
              out inspections with impartiality, accuracy, and reliability. The
              standard is applicable to all types of inspections, whether
              conformity assessments for products, services, or processes, or
              ensuring compliance with regulatory standards in various
              industries.
            </p>
            <p className="text-gray-700 font-semibold text-lg mb-4">
              Key aspects of ISO/IEC 17020 certification include:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Technical Competence: Ensuring that inspection bodies have qualified personnel, appropriate equipment, and procedures to conduct inspections effectively.",
                "Impartiality and Independence: Establishing the independence of inspection bodies to guarantee that inspections are conducted without conflicts of interest, ensuring fair and unbiased results.",
                "Quality Management: Implementing a quality management system (QMS) to monitor and continuously improve inspection practices and outcomes.",
                "Accurate Documentation and Reporting: Ensuring that inspections are documented correctly, with clear, precise reports that provide actionable information to clients and regulatory authorities.",
              ].map((text, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{text}</span>
                </motion.div>
              ))}
            </div>
            <p className="text-gray-600 text-lg leading-relaxed mt-6">
              ISO/IEC 17020 is relevant for any organization that offers
              inspection services in industries where accurate, impartial, and
              reliable inspections are essential for maintaining safety,
              compliance, and quality standards.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of ISO/IEC 17020 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Users,
                title: "Competence of Personnel",
                desc: "The standard outlines requirements for the competence of personnel involved in inspection activities. This includes qualification criteria, training, and experience requirements to ensure that inspectors are fully capable of performing inspections accurately and efficiently.",
              },
              {
                icon: Award,
                title: "Impartiality and Independence",
                desc: "ISO/IEC 17020 requires inspection bodies to establish mechanisms that ensure impartiality and independence in the inspection process. This prevents conflicts of interest and ensures that inspection results are unbiased and trustworthy.",
              },
              {
                icon: ShieldCheck,
                title: "Quality Management System (QMS)",
                desc: "ISO/IEC 17020 requires inspection bodies to implement a quality management system (QMS) that supports continuous improvement. The QMS helps ensure consistent, high-quality inspection services by managing procedures, documents, and records effectively.",
              },
              {
                icon: FileCheck,
                title: "Clear Documentation and Reporting",
                desc: "The standard emphasizes the importance of clear, accurate documentation and inspection reports that provide clients with actionable information. This includes details on the inspection process, findings, and any non-conformities identified during the inspection.",
              },
              {
                icon: Target,
                title: "Risk-Based Approach",
                desc: "ISO/IEC 17020 encourages the application of a risk-based approach to inspections, helping organizations identify potential risks and take appropriate actions to mitigate them, ensuring that inspections are thorough and effective.",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Matters */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why ISO/IEC 17020 Certification Matters for Your Business
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Demonstrates Competence — ISO/IEC 17020 certification demonstrates that your inspection body has the necessary technical competence to conduct inspections reliably and accurately. This is critical for building trust with clients, stakeholders, and regulatory authorities.",
                "Ensures Impartiality — By meeting ISO/IEC 17020’s impartiality requirements, your inspection body can offer unbiased assessments that clients can rely on, ensuring that inspection results are transparent and fair.",
                "Improved Client Confidence — Achieving ISO/IEC 17020 certification boosts your reputation and instills confidence in your clients, ensuring them that your inspections are performed in accordance with international standards and best practices.",
                "Regulatory Compliance — For many industries, inspection is a legal or regulatory requirement. ISO/IEC 17020 certification helps your inspection body meet compliance requirements, ensuring that inspections meet industry regulations and standards.",
                "Competitive Advantage — ISO/IEC 17020 certification provides a competitive edge in the marketplace, signaling to potential clients that your inspection body adheres to high-quality standards and offers trustworthy, impartial, and reliable inspection services.",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Does Include */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Does ISO/IEC 17020 Certification Include?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              ISO/IEC 17020 outlines the necessary requirements for ensuring the
              competence and impartiality of inspection bodies. The key elements
              of the certification include:
            </p>
            <div className="space-y-4">
              {[
                "1. Competence of Inspection Personnel — Inspectors must meet specific qualification, training, and experience requirements to ensure they have the necessary skills to perform inspections accurately and reliably.",
                "2. Impartiality and Independence — The inspection body must demonstrate that it operates independently from any external influence that could compromise the integrity of the inspection process. This ensures that inspections are objective and unbiased.",
                "3. Quality Management System (QMS) — The inspection body must implement a QMS that monitors and continuously improves its inspection practices, ensuring consistent, high-quality service delivery. The QMS should include clear procedures for document control, non-conformity handling, and audit management.",
                "4. Inspection Procedures — ISO/IEC 17020 specifies that inspection bodies must have documented inspection procedures that outline the steps involved in conducting inspections. These procedures ensure that inspections are carried out consistently and in compliance with established standards.",
                "5. Reporting and Documentation — The standard ensures that inspection bodies maintain clear and accurate records of their activities. This includes providing clients with detailed inspection reports that include findings, recommendations, and any non-conformities identified.",
                "6. Periodic Evaluation and Audit — ISO/IEC 17020 requires inspection bodies to undergo periodic internal audits and evaluations to ensure the effectiveness of their quality management system and the continual improvement of their inspection services.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Benefits of ISO/IEC 17020 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Increased Trust and Credibility",
                "Consistent Inspection Services",
                "Regulatory Compliance",
                "Improved Efficiency and Risk Management",
                "Market Differentiation",
              ].map((adv, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mb-3"
                  />
                  <p className="text-gray-700 font-medium text-sm">{adv}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Achieve ISO/IEC 17020 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              Achieving ISO/IEC 17020 certification involves the following key
              steps:
            </p>
            <div className="space-y-4">
              {[
                "1. Develop and Document Inspection Procedures — Establish clear inspection procedures that ensure consistency and accuracy in the inspection process. Document all processes, including qualifications for personnel and steps for performing inspections.",
                "2. Ensure Competence of Personnel — Train and qualify inspection personnel to meet the necessary skills, experience, and training requirements outlined by ISO/IEC 17020.",
                "3. Implement a Quality Management System (QMS) — Develop and implement a QMS that manages and improves inspection activities, including documentation control, non-conformity management, and internal audits.",
                "4. Conduct Internal Audits and Review — Conduct regular internal audits to ensure that your inspection body’s procedures and controls are working effectively. Continuously review and improve your inspection services to ensure compliance with ISO/IEC 17020.",
                "5. Engage an Accredited Certification Body — Work with an accredited certification body to conduct a third-party audit and assess your inspection body’s compliance with the ISO/IEC 17020 standard.",
                "6. Maintain Ongoing Compliance — Once certified, maintain ongoing compliance with ISO/IEC 17020 by conducting regular internal audits, maintaining inspection documentation, and continuously improving your processes.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for ISO/IEC 17020 Certification?
            </h2>
            <div className="w-20 h-1 bg-orange-500 rounded-full mx-auto mb-6" />
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. provides expert ISO/IEC 17020
              certification consultancy services, helping inspection bodies
              implement and maintain competent, impartial, and reliable
              inspection practices that meet the ISO/IEC 17020 requirements.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-300 font-semibold text-lg text-center mb-6">
              Our services include:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "Inspection Procedures Development",
                "Competence Assessment and Training",
                "Quality Management System (QMS) Implementation",
                "Internal Audit and Pre-certification Support",
                "Third-Party Certification Assistance",
                "Continuous Monitoring and Improvement",
              ].map((service, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {service}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-300 text-base leading-relaxed max-w-3xl mx-auto">
              We help inspection bodies achieve ISO/IEC 17020 certification,
              ensuring impartiality, competence, and reliability in all
              inspection activities.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ISO17020_Details;
